import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  Dialog,
  DialogActionsBar,
  ActionsLayout,
} from '@progress/kendo-react-dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Grid, createStyles, makeStyles } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import {
  addItemsGroups,
  deleteItemsGroups,
  editItemsGroups,
  getItemsGroups,
} from '../../store/NBC/NBCAction';
import { NBCProfile } from '../../Interfaces/NBC/NBCItem';
import { ApplicationState } from '../../store';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Error, Label } from '@progress/kendo-react-labels';
import _ from 'lodash';
import { NBCItemGroups } from '../../Interfaces/NBC/NBCItemGroups';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingPanel } from '../LoadingPanel';

const useStyles = makeStyles(theme =>
  createStyles({
    listBoxGrid: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    listBoxContainer: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'stretch',
      width: '100%',
      marginTop: '8px',
      marginBottom: '16px',
      height: '80%',
    },
    listBox: {
      width: '95%',
      height: '100%',
      backgroundColor: '#fff',
      overflowY: 'auto',
      border: '1px solid #d1d1d1',
      borderRadius: '8px',
      padding: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    listItem: {
      width: '100%',
    },
    listItemContent: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      width: '100%',
    },
    addButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '12px',
    },

    inputContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    listInput: {
      flex: 1,
      minWidth: '200px',
    },
    listIcons: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '4px',
    },
    errorMessage: {
      color: 'red',
      fontSize: '0.8rem',
      marginTop: '4px',
      marginLeft: '4px',
    },
  })
);

interface Props {
  handleCloseDialog: (open: boolean) => void;
}

const ManageProfileGroupsDialog: React.FC<Props> = ({
  handleCloseDialog,
}): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const itemsGroups: NBCItemGroups[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.groups
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.nbcReducer.loadingGroups
  );

  const selectedProfile: string | undefined = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedProfile
  );

  const profileNames: NBCProfile[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.profileNames
  );

  const [profileID, setProfileID] = React.useState<any>();

  React.useEffect(() => {
    const profileId = profileNames.find(
      item => item.ProfileUuid === selectedProfile
    )?.ProfileId;

    if (profileId !== undefined) {
      getItemsGroups(dispatch, profileId);
    }

    setProfileID(profileId);
  }, [selectedProfile]);

  const ERRORMSG = GetString('GROUP_ERROR_MSG');

  const GROUP_NAME = GetString('GROUP_NAME');

  const CANCEL_BUTTON = GetString('CANCEL_BUTTON');

  const ADD_BUTTON = GetString('ADD_BUTTON');

  const ADD_NEW_GROUP = GetString('ADD_NEW_GROUP');

  const YES = GetString('YES');

  const NO = GetString('NO');

  const CONFIRM_MESSAGE = GetString('CONFIRM_DELETE_MESAGE');

  const [layout, setLayout] = React.useState<ActionsLayout>('end');

  const [selectedGroupName, setSelectedGroupName] =
    React.useState<NBCItemGroups>();

  const [confirmVisible, setConfirmVisible] = React.useState<boolean>(false);

  const [addGroupVisible, setAddGroupVisible] = React.useState<boolean>(false);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const [enabledSaveButton, setEnabledSaveButton] =
    React.useState<boolean>(true);

  const [confirmDelete, setIsConfirmDelete] = React.useState<boolean>(false);

  const [deleteGroup, setDeleteGroup] = React.useState<
    NBCItemGroups | undefined
  >();

  const [groupName, setGroupName] = React.useState<any>();

  const [isGroupNameExist, setIsGroupNameExist] =
    React.useState<boolean>(false);

  const groupNameOnChange = (e: InputChangeEvent) => {
    const newGroupName = e.target.value;
    setGroupName(newGroupName);

    const groupNameExists = itemsGroups.some(
      groups => groups.GroupName === newGroupName
    );

    const isSameAsSelectedGroupName =
      newGroupName === selectedGroupName?.GroupName;

    setIsGroupNameExist(groupNameExists && !isSameAsSelectedGroupName);
    setEnabledSaveButton(isSameAsSelectedGroupName);
  };

  const saveOnClick = () => {
    if (profileID) {
      editItemsGroups(
        dispatch,
        profileID,
        selectedGroupName?.GroupUuid,
        groupName
      );
    }

    setEnabledSaveButton(true);
  };

  React.useEffect(() => {
    if (confirmDelete) {
      deleteItemsGroups(dispatch, profileID, deleteGroup?.GroupUuid);
      setIsConfirmDelete(false);
    }
  }, [confirmDelete]);

  const handleConfirm = () => {
    setIsConfirmDelete(true);
    setConfirmVisible(false);
  };

  const handleCancelConfirm = () => {
    setConfirmVisible(false);
  };

  const handleEdit = (value: NBCItemGroups) => {
    setSelectedGroupName(value);
  };

  const handleDelete = (value: NBCItemGroups) => {
    setConfirmVisible(true);
    setDeleteGroup(value);
  };

  const [newGroup, setNewGroup] = React.useState<any>();

  const [enableAddGroupButton, setEnableAddGroupButton] =
    React.useState<boolean>(true);

  const inputOnChange = (e: InputChangeEvent) => {
    const value = e.target.value;
    setNewGroup(value);

    const groupNameExists = itemsGroups.some(
      groups => groups.GroupName === value
    );

    setIsGroupNameExist(groupNameExists);
    setEnableAddGroupButton(value === '' || groupNameExists);
  };

  const handleSaveNewGroup = () => {
    if (profileID) {
      addItemsGroups(dispatch, profileID, newGroup);
    }
    setAddGroupVisible(false);
    setNewGroup('');
  };

  return (
    <>
      <Dialog
        style={{ zIndex: 10000 }}
        height="75vh"
        minWidth="25vw"
        title={
          <StringResourceManager
            variant="body1"
            style="commandLabel"
            stringKey={'MANAGE_GROUPS'}
            gutterBottom={true}
          />
        }
        onClose={handleClose}
      >
        <Grid className={classes.listBoxGrid}>
          <Grid>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'SELECT_GROUP'}
              gutterBottom={true}
            />
          </Grid>
          {loading && LoadingPanel}
          <Grid className={classes.listBoxContainer}>
            <List className={classes.listBox}>
              {itemsGroups.map((groups: NBCItemGroups) => (
                <ListItem key={groups.GroupUuid} className={classes.listItem}>
                  <div className={classes.listItemContent}>
                    <div className={classes.inputContainer}>
                      <Input
                        defaultValue={groups.GroupName}
                        onChange={groupNameOnChange}
                        className={classes.listInput}
                        onClick={() => handleEdit(groups)}
                      />
                      {groups.GroupName === selectedGroupName?.GroupName &&
                        isGroupNameExist && (
                          <div className={classes.errorMessage}>{ERRORMSG}</div>
                        )}
                    </div>

                    <div className={classes.listIcons}>
                      <IconButton
                        onClick={saveOnClick}
                        disabled={
                          groups.GroupUuid !== selectedGroupName?.GroupUuid ||
                          enabledSaveButton
                        }
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(groups)}>
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid className={classes.addButtonContainer}>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={() => setAddGroupVisible(true)}
            >
              {ADD_NEW_GROUP}
            </Button>
          </Grid>
        </Grid>

        <DialogActionsBar layout={layout}>
          <Button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            style={{ width: 100 }}
            onClick={handleClose}
          >
            {GetString('OK_BUTTON')}
          </Button>
        </DialogActionsBar>
      </Dialog>

      {addGroupVisible && (
        <Dialog
          height="25vh"
          minWidth="20vw"
          title={
            <StringResourceManager
              variant="body1"
              style="commandLabel"
              stringKey={'ADD_NEW_GROUP'}
              gutterBottom={true}
            />
          }
          onClose={() => {
            setAddGroupVisible(false);
            setNewGroup('');
          }}
        >
          <Grid>
            <div
              className={'k-form-field-wrap'}
              style={{ marginBottom: '10px' }}
            >
              <Label>{GROUP_NAME}</Label>
              <br />
              <Input
                onChange={inputOnChange}
                value={newGroup}
                style={{
                  borderColor: isGroupNameExist ? 'red' : 'black',
                  width: '260px',
                }}
              />
              {isGroupNameExist && (
                <Error direction={'start'}>{ERRORMSG}</Error>
              )}
            </div>
          </Grid>
          <DialogActionsBar layout={layout}>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              style={{ width: 100 }}
              onClick={() => {
                setAddGroupVisible(false);
                setNewGroup('');
              }}
            >
              {CANCEL_BUTTON}
            </Button>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              style={{ width: 100 }}
              onClick={handleSaveNewGroup}
              disabled={enableAddGroupButton}
            >
              {ADD_BUTTON}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {confirmVisible && (
        <Dialog
          style={{ zIndex: 10001 }}
          title="Confirmation"
          onClose={handleCancelConfirm}
        >
          <p>{CONFIRM_MESSAGE}</p>
          <DialogActionsBar>
            <Button onClick={handleConfirm}>{YES}</Button>
            <Button onClick={handleCancelConfirm}>{NO}</Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default ManageProfileGroupsDialog;
